import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { CaretRightIcon } from '@buzzfeed/react-components';
import styles from './headline-breadcrumb.module.scss';
import { useUnitTracking } from '../../../../hooks/analytics/useUnitTracking';
import BuzzContext from '../../../../contexts/buzz';
import { urlToId } from '../../../../utils/url';
import { isNews } from '../../../../utils/isNews';
import { isGamesPost } from '../../../../utils/games';

const separators = {
  comma: (
    <span className={styles.comma} aria-hidden="true">
      ,{' '}
    </span>
  ),
  caret: (
    <CaretRightIcon
      className={`caret-separator ${styles.caret}`}
      aria-hidden="true"
    />
  ),
  midot: (
    <span className={`bold ${styles.midot}`} aria-hidden="true">&#183;</span>
  ),
  bullet: (
    <span className={styles.bullet} aria-hidden="true">•</span>
  ),
};

const getSeparator = (buzz) => {
  let separator;

  switch (true) {
    case buzz.isNews:
      separator = 'bullet';
      break;
    case buzz.isQuiz:
      separator = 'midot';
      break;
    default:
      separator = 'caret';
  }

  return separators[separator];
}

function Breadcrumb({ buzz, text, url, index }) {
  const unitTrackingData = useMemo(
    () => ({
      eventCategory: 'Header',
      eventAction: 'link',
      eventLabel: url,
      unit_type: 'buzz_head',
      unit_name: buzz.id,
      item_name: isNews(buzz) ? text : urlToId(url),
      item_type: 'text',
      position_in_unit: isNews(buzz) ? null : index,
      position_in_subunit: isNews(buzz) ? index : null,
      subunit_name: isNews(buzz) ? 'breadcrumbs' : '',
      subunit_type: isNews(buzz) ? 'component' : '',
      target_content_type: 'feed',
      target_content_id: urlToId(url),
    }),
    [buzz, index, text, url]
  );
  const unitEl = useUnitTracking(unitTrackingData);

  return (
    <a ref={unitEl} className={`metadata-link ${styles.breadcrumbLink}`} href={url}>
      {text}
    </a>
  );
}

function BreadcrumbList({
  breadcrumbs,
  separator,
  buzz,
}) {
  return (
    <>
      {breadcrumbs.map((item, index) => {
        const isLastItem = breadcrumbs.length === index + 1;
        return (
          <li className={styles.breadcrumbItem} key={`${item}--${index}`}>
            <Breadcrumb buzz={buzz} text={item.text} url={item.url} index={index} />
            {!isLastItem && separator}
          </li>
        );
      })}
    </>
  );
}

export function HeadlineBreadcrumb({
  breadcrumbs,
  secondaryBreadcrumbs = [],
  designUpdateEnabled = false,
  useQuizBreadcrumbStyle = false,
}) {
  const { buzz = {} } = useContext(BuzzContext) || {};
  const separator = designUpdateEnabled ? null : getSeparator(buzz);
  const olClassNames = [
    designUpdateEnabled ? styles.breadcrumbListRedesign : styles.breadcrumbList,
    useQuizBreadcrumbStyle && styles.breadcrumbListQuiz,
    buzz.isNews && styles.breadcrumbListNews,
    isGamesPost(buzz) && styles.breadcrumbListGames,
  ].filter(Boolean).join(' ');

  return (
    <ol className={olClassNames}>
      <BreadcrumbList
        breadcrumbs={[...breadcrumbs, ...secondaryBreadcrumbs]}
        separator={separator}
        buzz={buzz}
      />
    </ol>
  );
}

HeadlineBreadcrumb.propTypes = {
  breadcrumbs: PropTypes.array.isRequired,
  secondaryBreadcrumbs: PropTypes.array,
  designUpdateEnabled: PropTypes.bool,
  useQuizBreadcrumbStyle: PropTypes.bool,
};
